import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { CheckCircle, AlertCircle, Copy, XCircle, Search } from 'lucide-react';
import { collection, query, where, orderBy, limit, startAfter, getDocs, getCountFromServer } from 'firebase/firestore';
import { db } from '../../firebase.js';
import '../../styles/GestionDeRetiros.css';
import { useAdminContext } from '../../context/AdminContext.js';
import Pagination from '../../components/common/Pagination.js';

const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

const normalizeTimestamp = (input) => {
    if (!input) return null;
    if (typeof input.toDate === 'function') {
        try {
            const asDate = input.toDate();
            return isValidDate(asDate) ? asDate : null;
        } catch {
            return null;
        }
    }
    if (input instanceof Date) {
        return isValidDate(input) ? input : null;
    }
    const parsed = new Date(input);
    return isValidDate(parsed) ? parsed : null;
};

const getFirstApprovedDate = (request, userProfile) => {
    const candidates = [
        request?.userFirstApprovedAt,
        request?.firstAgreementApprovedAt,
        request?.firstApprovedAgreementAt,
        request?.firstApprovedAt,
        userProfile?.userFirstApprovedAt,
        userProfile?.firstAgreementApprovedAt,
        userProfile?.firstApprovedAgreementAt,
        userProfile?.firstApprovedAt,
    ];

    for (const candidate of candidates) {
        const date = normalizeTimestamp(candidate);
        if (date) {
            return date;
        }
    }

    return null;
};

const formatCurrency = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) {
        return '$0.00';
    }
    return `$${amount.toFixed(2)}`;
};

// --- COMPONENTE MEJORADO PARA MOSTRAR EL ESTADO PAGABLE ---
const PayableStatus = ({ status, firstApprovedDate, reviewedAt }) => {
    if (status !== 'pendiente') {
        const processedAt = normalizeTimestamp(reviewedAt);
        const formatted = processedAt ? processedAt.toLocaleString('es-ES') : 'Sin fecha';

        if (status === 'aprobado') {
            return (
                <span className="payable-status success processed" title={`Pagado el ${formatted}`}>
                    <CheckCircle size={16} />
                    <span>
                        <strong>Pagado</strong>
                        <small>{formatted}</small>
                    </span>
                </span>
            );
        }

        if (status === 'rechazado') {
            return (
                <span className="payable-status rejected processed" title={`Revisado el ${formatted}`}>
                    <XCircle size={16} />
                    <span>
                        <strong>Rechazado</strong>
                        <small>{formatted}</small>
                    </span>
                </span>
            );
        }

        return <span className="payable-status neutral"><AlertCircle size={16} /> {formatted}</span>;
    }

    if (!firstApprovedDate) {
        return (
            <span className="payable-status waiting" title="No hay registro de un acuerdo aprobado para este usuario.">
                <AlertCircle size={16} /> Sin dato
            </span>
        );
    }

    const now = new Date();
    const hoursDiff = (now.getTime() - firstApprovedDate.getTime()) / (1000 * 60 * 60);

    if (hoursDiff >= 72) {
        return <span className="payable-status success"><CheckCircle size={16} /> Pagable</span>;
    }

    const hoursRemaining = Math.max(0, Math.ceil(72 - hoursDiff));
    const tooltip = `Faltan aproximadamente ${hoursRemaining} hora${hoursRemaining === 1 ? '' : 's'} para liberar el retiro.`;

    return (
        <span className="payable-status waiting" title={tooltip}>
            <AlertCircle size={16} /> En espera · {hoursRemaining}h
        </span>
    );
};

const RequestRow = ({ request, users, onApprove, onReject, onCopy, processingId }) => {
    const userProfile = users.find(u => u.id === request.userId);
    const firstApprovedDate = useMemo(() => getFirstApprovedDate(request, userProfile), [request, userProfile]);

    const isPayable = (() => {
        if (request.status !== 'pendiente' || !firstApprovedDate) {
            return false;
        }
        const now = new Date();
        const hoursDiff = (now.getTime() - firstApprovedDate.getTime()) / (1000 * 60 * 60);
        return hoursDiff >= 72;
    })();

    return (
        <tr className={`main-row ${isPayable ? 'payable-row' : ''}`}>
            <td data-label="Email">
                <span className="user-email">{request.userEmail}</span>
            </td>
            <td data-label="Nombre">
                <span className="user-name">{userProfile?.fullName || 'Nombre no disponible'}</span>
            </td>
            <td data-label="Montos">
                <div className="amount-info-cell" title={`Solicitado: ${formatCurrency(request.amount)}\nA transferir: ${formatCurrency(request.netAmount ?? request.amount)}`}>
                    <div className="amount-line requested">
                        <span className="amount-label">Solicitado</span>
                        <span className="amount-value">{formatCurrency(request.amount)}</span>
                    </div>
                    <div className="amount-line net">
                        <span className="amount-label">A transferir</span>
                        <span className="amount-value">{formatCurrency(request.netAmount ?? request.amount)}</span>
                    </div>
                </div>
            </td>
            <td data-label="Billetera" className="wallet-cell">
                <div className="wallet-container">
                    <span className="wallet-address" title={request.walletAddress}>{request.walletAddress ? `${request.walletAddress.substring(0, 10)}...` : 'N/A'}</span>
                    {request.walletAddress && (
                        <button onClick={() => onCopy(request.walletAddress)} className="copy-wallet-btn" title="Copiar Billetera">
                            <Copy size={16} />
                        </button>
                    )}
                </div>
            </td>
            <td data-label="Fecha Solicitud">{request.createdAt?.toDate ? request.createdAt.toDate().toLocaleString('es-ES') : 'N/A'}</td>
            <td data-label="Estado de Pago">
                <PayableStatus status={request.status} firstApprovedDate={firstApprovedDate} reviewedAt={request.reviewedAt} />
            </td>
            <td data-label="Acciones" className="action-buttons-cell">
                {request.status === 'pendiente' ? (
                    <>
                        <button onClick={() => onApprove(request)} disabled={processingId === request.id} className="action-btn approve" title="Aprobar Retiro">
                            <CheckCircle size={16} />
                        </button>
                        <button onClick={() => onReject(request)} disabled={processingId === request.id} className="action-btn reject" title="Rechazar Retiro">
                            <XCircle size={16} />
                        </button>
                    </>
                ) : request.status === 'aprobado' ? (
                    <span className="action-status-badge approved"><CheckCircle size={16} /> Aprobado</span>
                ) : request.status === 'rechazado' ? (
                    <span className="action-status-badge rejected"><XCircle size={16} /> Rechazado</span>
                ) : null}
            </td>
        </tr>
    );
};

// Trae una sola página de `withdraw_requests` directo de Firestore, filtrada por estado.
// Reemplaza al viejo listener global (capado a las 50 solicitudes más recientes de TODO el
// sitio) que hacía que las solicitudes viejas — sobre todo las pendientes sin resolver, que
// nunca se sacan de en medio — dejaran de verse cuando se acumulaban 50 solicitudes nuevas.
const fetchWithdrawalPage = async (status, pageSize, cursor) => {
    const constraints = [];
    if (status !== 'todos') constraints.push(where('status', '==', status));
    constraints.push(orderBy('createdAt', 'desc'));
    if (cursor) constraints.push(startAfter(cursor));
    constraints.push(limit(pageSize));
    const snap = await getDocs(query(collection(db, 'withdraw_requests'), ...constraints));
    return {
        items: snap.docs.map(d => ({ id: d.id, ...d.data() })),
        lastDoc: snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null,
    };
};

const fetchWithdrawalCount = async (status) => {
    const q = status === 'todos'
        ? query(collection(db, 'withdraw_requests'))
        : query(collection(db, 'withdraw_requests'), where('status', '==', status));
    const snap = await getCountFromServer(q);
    return snap.data().count;
};

const GestionDeRetiros = () => {
    const { users, settings, processingId, handleProcessWithdrawal, handleToggleWithdrawals } = useAdminContext();

    const [filterStatus, setFilterStatus] = useState('pendiente');
    const [activeTab, setActiveTab] = useState('pendiente');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Paginación real contra Firestore: página siguiente/anterior, no un array cacheado.
    // pageCache guarda las páginas ya visitadas EN ESTA SESIÓN (así "Anterior" no vuelve a
    // pedirle nada al servidor), indexadas por número de página (0 = primera).
    const [pageIndex, setPageIndex] = useState(0);
    const [pageCache, setPageCache] = useState({});
    const [totalCount, setTotalCount] = useState(0);
    const [pageLoading, setPageLoading] = useState(true);
    const requestIdRef = useRef(0);

    const loadFirstPage = useCallback(async (status, pageSize) => {
        const requestId = ++requestIdRef.current;
        setPageLoading(true);
        try {
            const [count, page] = await Promise.all([
                fetchWithdrawalCount(status),
                fetchWithdrawalPage(status, pageSize, null),
            ]);
            if (requestId !== requestIdRef.current) return; // llegó una respuesta vieja, se descarta
            setTotalCount(count);
            setPageCache({ 0: page });
            setPageIndex(0);
        } catch (err) {
            console.error('Error cargando retiros:', err);
            Swal.fire('Error', 'No se pudieron cargar los retiros.', 'error');
        } finally {
            if (requestId === requestIdRef.current) setPageLoading(false);
        }
    }, []);

    // Recarga desde la página 1 cada vez que cambia el tab/estado o las filas por página.
    useEffect(() => {
        loadFirstPage(filterStatus, itemsPerPage);
    }, [filterStatus, itemsPerPage, loadFirstPage]);

    const handleNextPage = async () => {
        const nextIndex = pageIndex + 1;
        if (pageCache[nextIndex]) { setPageIndex(nextIndex); return; }
        const cursor = pageCache[pageIndex]?.lastDoc;
        if (!cursor) return;
        setPageLoading(true);
        try {
            const page = await fetchWithdrawalPage(filterStatus, itemsPerPage, cursor);
            setPageCache(prev => ({ ...prev, [nextIndex]: page }));
            setPageIndex(nextIndex);
        } catch (err) {
            console.error('Error cargando la siguiente página de retiros:', err);
            Swal.fire('Error', 'No se pudo cargar la siguiente página.', 'error');
        } finally {
            setPageLoading(false);
        }
    };

    const handlePreviousPage = () => {
        if (pageIndex > 0) setPageIndex(pageIndex - 1);
    };

    // Los filtros de búsqueda/fecha se aplican sobre la página actual (igual que antes), no
    // contra toda la base — buscar algo que esté en otra página todavía requiere paginar hasta
    // encontrarlo.
    const filteredRequests = useMemo(() => {
        const currentPageItems = pageCache[pageIndex]?.items || [];
        return currentPageItems.filter(req => {
            const reqDate = req.createdAt?.toDate();
            const searchMatch = !searchTerm || req.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) || req.walletAddress?.toLowerCase().includes(searchTerm.toLowerCase());
            const startDateMatch = !filterStartDate || (reqDate && reqDate >= new Date(filterStartDate));
            const endDateMatch = !filterEndDate || (reqDate && reqDate <= new Date(filterEndDate + 'T23:59:59'));
            return searchMatch && startDateMatch && endDateMatch;
        });
    }, [pageCache, pageIndex, searchTerm, filterStartDate, filterEndDate]);

    const paginatedRequests = filteredRequests;
    const currentPage = pageIndex + 1;
    const canNextPage = totalCount > (pageIndex + 1) * itemsPerPage;
    const canPreviousPage = pageIndex > 0;

    // Después de aprobar/rechazar, el estado de esa solicitud cambia en el servidor (por
    // ejemplo, sale del tab "Pendientes") — se vuelve a la página 1 del tab actual para reflejarlo,
    // en vez de dejar la fila desactualizada en la página que se estaba viendo.
    const reloadAfterProcessing = () => loadFirstPage(filterStatus, itemsPerPage);
    
    const handleApprove = (request) => {
        Swal.fire({
            title: `Aprobar Retiro de $${request.amount.toFixed(2)}`,
            html: `
                <div>Usuario: ${request.userEmail}</div>
                <div style="margin-top: 10px;">Billetera: <code style="user-select: all; background-color: #f0f0f0; padding: 2px 4px; border-radius: 4px;">${request.walletAddress}</code></div>
            `,
            input: 'text', inputLabel: 'ID de Transacción (TxID)', inputPlaceholder: 'Pega aquí el hash...',
            inputAttributes: { required: 'true' }, showCancelButton: true, confirmButtonText: 'Confirmar y Pagar',
            confirmButtonColor: 'var(--verde-botones)',
            preConfirm: (txId) => {
                if (!txId) { Swal.showValidationMessage('El TxID es obligatorio.'); return false; }
                return txId;
            }
        }).then(async result => {
            if (!result.isConfirmed) return;
            await handleProcessWithdrawal(request, 'aprobado', result.value);
            reloadAfterProcessing();
        });
    };

    const handleReject = (request) => {
        Swal.fire({
            title: `Rechazar Retiro de $${request.amount.toFixed(2)}`, input: 'text',
            inputLabel: 'Motivo del Rechazo', showCancelButton: true,
            confirmButtonText: 'Sí, Rechazar', confirmButtonColor: '#dc3545',
        }).then(async result => {
            if (!result.isConfirmed) return;
            await handleProcessWithdrawal(request, 'rechazado', result.value || 'Sin motivo especificado');
            reloadAfterProcessing();
        });
    };

    const handleCopy = async (walletAddress) => {
        try {
            await navigator.clipboard.writeText(walletAddress);
            Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: '¡Billetera copiada!', showCancelButton: false, timer: 2000 });
        } catch (err) {
            console.error('Error al copiar la billetera: ', err);
            Swal.fire({ toast: true, position: 'top-end', icon: 'error', title: 'No se pudo copiar', showCancelButton: false, timer: 2000 });
        }
    };

    if (pageLoading && Object.keys(pageCache).length === 0) return <p>Cargando retiros...</p>;

    return (
        <div className="main-panel">
            <div className="withdrawal-header">
                <h2 className="section-title">Gestión de Retiros</h2>
                <div className="master-toggle" title={settings?.withdrawalsEnabled ? 'Clic para deshabilitar retiros' : 'Clic para habilitar retiros'}>
                    <span>Retiros Globales:</span>
                    <label className="toggle-switch">
                        <input type="checkbox" 
                            checked={settings?.withdrawalsEnabled || false} 
                            onChange={() => handleToggleWithdrawals(!settings?.withdrawalsEnabled)} />
                        <span className="toggle-knob"></span>
                    </label>
                </div>
            </div>
            
            <div className="report-filters">
                <div className="filter-item">
                    <Search size={16} />
                    <input type="text" placeholder="Buscar por Email o Billetera..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                <div className="filter-item">
                    <select value={filterStatus} onChange={(e) => { setActiveTab(e.target.value); setFilterStatus(e.target.value); }} style={{ border: 'none', background: 'transparent', outline: 'none' }}>
                        <option value="pendiente">Pendientes</option>
                        <option value="aprobado">Aprobados</option>
                        <option value="rechazado">Rechazados</option>
                        <option value="todos">Todos</option>
                    </select>
                </div>
                <div className="filter-item">
                    <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} style={{ border: 'none', background: 'transparent', outline: 'none' }} />
                </div>
                 <div className="filter-item">
                    <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} style={{ border: 'none', background: 'transparent', outline: 'none' }} />
                </div>
            </div>

            <div className="admin-tabs retiro-tabs" style={{ borderBottom: '2px solid #e5e7eb', marginBottom: 0 }}>
                <button onClick={() => { setActiveTab('pendiente'); setFilterStatus('pendiente'); }} className={activeTab === 'pendiente' ? 'active' : ''}>Pendientes</button>
                <button onClick={() => { setActiveTab('aprobado'); setFilterStatus('aprobado'); }} className={activeTab === 'aprobado' ? 'active' : ''}>Aprobados</button>
                <button onClick={() => { setActiveTab('rechazado'); setFilterStatus('rechazado'); }} className={activeTab === 'rechazado' ? 'active' : ''}>Rechazados</button>
            </div>

            <div className="report-table-container">
                <table className="report-table">
                    <thead>
                        <tr>
                            <th>Email</th>
                            <th>Nombre</th>
                            <th>Montos (Solicitado / Neto)</th>
                            <th>Billetera</th>
                            <th>Fecha Solicitud</th>
                            <th>Estado de Pago</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRequests.length > 0 ? (
                            paginatedRequests.map(req => (
                                <RequestRow
                                    key={req.id}
                                    request={req}
                                    users={users}
                                    onApprove={handleApprove}
                                    onReject={handleReject}
                                    onCopy={handleCopy}
                                    processingId={processingId}
                                />
                            ))
                        ) : (
                            <tr><td colSpan={7} style={{ textAlign: 'center' }}>
                                {activeTab === 'completados' ? 'No hay retiros completados.' : 'No hay retiros que coincidan con los filtros.'}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Pagination
                totalItems={totalCount}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
                currentPage={currentPage}
                onNext={handleNextPage}
                onPrevious={handlePreviousPage}
                canNext={canNextPage}
                canPrevious={canPreviousPage}
            />
            {pageLoading && Object.keys(pageCache).length > 0 && (
                <p style={{ textAlign: 'center', color: 'var(--gris-texto)', marginTop: '8px' }}>Cargando...</p>
            )}
        </div>
    );
};

export default GestionDeRetiros;